import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Send, X, MessageSquare } from 'lucide-react';

const ChatBox = ({ leadId, companyId, onClose }) => { // Added companyId prop
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const bottomRef = useRef(null);
  const role = localStorage.getItem('role');

  useEffect(() => {
    if(!leadId) return;
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [leadId, companyId]); // Reload if companyId changes

  const fetchMessages = async () => {
    try {
      // If admin, pass the company_id we want to talk to
      const url = companyId 
        ? `chat/lead/${leadId}/?company_id=${companyId}`
        : `chat/lead/${leadId}/`;
        
      const res = await api.get(url);
      if (res.data.messages) {
        setMessages(res.data.messages);
      }
    } catch (error) {
      console.error("Chat Error", error);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    try {
      // Pass company_id in body if Admin
      await api.post(`chat/lead/${leadId}/`, { 
        text: newMessage,
        company_id: companyId 
      });
      setNewMessage('');
      fetchMessages(); 
    } catch (error) {
      console.error("Send failed", error);
    }
  };

  const isMe = (msg) => {
    if (Number(role) === 1) return msg.is_admin;
    return !msg.is_admin;
  };

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-t-xl shadow-2xl border border-gray-200 z-[9999] flex flex-col h-[500px] font-sans">
      <div className="bg-[#2E86DE] text-white p-4 rounded-t-xl flex justify-between items-center shadow-md">
        <div className="flex items-center">
          <div className="bg-white/20 p-2 rounded-full mr-3"><MessageSquare size={20} /></div>
          <div>
            <span className="font-bold block text-sm">Support Chat</span>
            <span className="text-xs text-blue-100">Lead #{leadId}</span>
          </div>
        </div>
        <button onClick={onClose} className="hover:bg-white/20 rounded-full p-1 transition"><X size={20} /></button>
      </div>

      <div className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 mt-10 text-sm">
            <p>No messages yet.</p>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${isMe(msg) ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] p-3 rounded-2xl text-sm shadow-sm ${
              isMe(msg)
                ? 'bg-blue-600 text-white rounded-br-none' 
                : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
            }`}>
              {msg.text}
              <div className={`text-[10px] mt-1 text-right ${isMe(msg) ? 'text-blue-200' : 'text-gray-400'}`}>
                {new Date(msg.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSend} className="p-3 border-t border-gray-200 bg-white flex items-center gap-2">
        <input 
          type="text" 
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type message..." 
          className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
        />
        <button type="submit" className="bg-[#2E86DE] text-white p-2.5 rounded-full hover:bg-blue-700 shadow-md transition-transform transform active:scale-95">
          <Send size={18} />
        </button>
      </form>
    </div>
  );
};

export default ChatBox;